import { usePlatformLocale } from "../lib/platform-language";
import { translateUi } from "../lib/i18n.ts";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

// Shared platform toast (AGENTS.md "Feedback And Toast Messages"). Bottom-right, restrained
// green/red, auto-dismisses. Any module wiring success/error feedback for a completed action
// should reuse this instead of a module-specific toast.
export type PlatformToastState = { kind: "success" | "error"; message: string } | null;

export function usePlatformToast(durationMs = 5000) {
  const [toast, setToast] = useState<PlatformToastState>(null);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), durationMs);
    return () => window.clearTimeout(id);
  }, [toast, durationMs]);

  const showSuccess = useCallback((message: string) => setToast({ kind: "success", message }), []);
  const showError = useCallback((message: string) => setToast({ kind: "error", message }), []);
  const dismiss = useCallback(() => setToast(null), []);

  return {
    toast,
    showSuccess,
    showError,
    dismiss,
  };
}

export function PlatformToast({ toast, onDismiss }: { toast: PlatformToastState; onDismiss: () => void }) {
  usePlatformLocale();
  if (!toast || typeof document === "undefined") return null;
  return createPortal((
    <div className={`platform-toast ${toast.kind}`} role={toast.kind === "success" ? "status" : "alert"}>
      <span>{toast.message}</span>
      <button type="button" onClick={onDismiss} aria-label={translateUi("Mbyll njoftimin", "Dismiss notification")}><X size={14} /></button>
    </div>
  ), document.body);
}
